// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import {
  type ColumnDef,
  type ColumnSizingState,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { RotateCcw, UnfoldHorizontal, X } from "lucide-react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import type { FilterValue } from "@/components/filter-popover";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Activity } from "@/hooks/api/use-account-activities";
import { useAccountOpportunities } from "@/hooks/api/use-account-opportunities";
import type {
  MilestoneAnalysis,
  TaskGroup,
} from "@/hooks/api/use-milestone-analyses";
import { useTeamDisplayNames } from "@/hooks/api/use-team-display-names";
import { useTouchpointPersons } from "@/hooks/api/use-touchpoint-persons";
import { useMilestoneAnalysisAdminEnabled } from "@/hooks/auth/use-permissions-gates";
import { useBrowserStorage } from "@/hooks/use-browser-storage";
import { useBuyingGroupSelection } from "@/hooks/use-buying-group-selection";
import { useThreadMetadata } from "@/hooks/use-thread-metadata";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { cn } from "@/lib/utils";
import { FilterModeDropdown } from "./components/filter-mode-dropdown";
import {
  getActivityColumns,
  useActivityTableColumns,
} from "./components/table-columns";
import { ActivityTableHeader } from "./components/table-header";
import { OpportunityStageTransitionRow } from "./components/table-opportunity-row";
import {
  ActivityRow,
  GapRowComponent,
  getRowClassName,
} from "./components/table-row";
import { useCollapsedData } from "./hooks/use-collapsed-data";
import { useCTAVisibility } from "./hooks/use-cta-visibility";
import { useScrollToDate } from "./hooks/use-scroll-to-date";
import { useVirtualizedRows } from "./hooks/use-virtualized-rows";
import { useVisibleRange } from "./hooks/use-visible-range";
import { ActivityDetailsSheet, isGroupedActivity } from "./side-sheet";
import type {
  ActivityTableRef,
  CollapsedActivity,
  FilterMode,
  GapRow,
  OpportunityTransitionItem,
} from "./types";

// Column sizing is handled via organization-scoped sessionStorage hook

import { ScrollCTA } from "./components/scroll-cta";
import {
  associateMilestoneAnalysesWithTransitions,
  filterActivityData,
  insertGapRows,
  mapDisplayFilters,
  mergeTransitionsWithActivities,
  processOpportunityTransitions,
} from "./utils";

// Updated ActivityTableProps with filter props
export interface ActivityTableProps {
  data?: Activity[];
  filteredActivities?: Activity[];
  isLoading?: boolean;
  accountId?: string;
  selectedOpportunityIds?: string[];
  onVisibleRangeChange?: (range: { start: string; end: string }) => void;
  onFilteredActivitiesChange?: (activities: Activity[]) => void;
  // Filter props now passed from parent
  filterMode: FilterMode;
  setFilterMode: (mode: FilterMode) => void;
  filters: Record<string, FilterValue>;
  updateFilter: (key: string, value: FilterValue) => void;
  clearFilter: (key: string) => void;
  clearAllFilters: () => void;
  // New props for displaying touchpoint counts
  totalTouchpoints?: number;
  onActivityClick?: (activity: Activity | CollapsedActivity) => void;
  // Milestone analyses data passed from parent
  milestoneTasks?: TaskGroup[];
  // Custom buying group opportunity IDs for minimap bars and touchpoint table
  customGroupOpportunityIds?: string[];
  // Optional overrides for Person Explorer
  storageKeyPrefix?: string;
  opportunitiesOverride?: import("@/hooks/api/use-account-opportunities").Opportunity[];
  getPersonNameOverride?: (personId: string) => string;
  getPersonEmailOverride?: (personId: string) => string;
  getNameByEmailOverride?: (email?: string | null) => string;
}

export const ActivityTable = forwardRef<ActivityTableRef, ActivityTableProps>(
  (
    {
      data = [],
      filteredActivities = [],
      isLoading,
      accountId,
      selectedOpportunityIds,
      onVisibleRangeChange,
      onFilteredActivitiesChange,
      // Filter props from parent
      filterMode,
      setFilterMode,
      filters,
      updateFilter,
      clearFilter,
      clearAllFilters,
      // Header props
      totalTouchpoints,
      onActivityClick,
      milestoneTasks,
      // Custom buying group opportunity IDs
      customGroupOpportunityIds,
      // Optional overrides for Person Explorer
      storageKeyPrefix,
      opportunitiesOverride,
      getPersonNameOverride,
      getPersonEmailOverride,
      getNameByEmailOverride,
    },
    ref,
  ) => {
    // Use the sidebar hook to get state
    const { state, isMobile } = useSidebar();

    // ===== State =====
    const [selectedActivity, setSelectedActivity] = useState<
      Activity | CollapsedActivity | null
    >(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [focusedActivityId, setFocusedActivityId] = useState<
      string | undefined
    >(undefined);
    // Store only the ID of the selected milestone analysis TaskGroup
    const [selectedMilestoneTaskId, setSelectedMilestoneTaskId] = useState<
      string | undefined
    >(undefined);
    const [selectedAnalysis, setSelectedAnalysis] = useState<
      MilestoneAnalysis | undefined
    >(undefined);
    const tableRef = useRef<HTMLDivElement>(null); // Ref for the scrollable div
    const milestoneAnalysisEnabled = useMilestoneAnalysisAdminEnabled();
    // Track visible date range for scroll features
    const [visibleDateRange, setVisibleDateRange] = useState<{
      start: string;
      end: string;
    } | null>(null);
    // Column sizing state stored in sessionStorage (ephemeral, no migration needed)
    const [columnSizing, setColumnSizing] =
      useBrowserStorage<ColumnSizingState>(
        storageKeyPrefix
          ? `${storageKeyPrefix}::touchpoints::columns::sizes`
          : "accounts::explorer::touchpoints::columns::sizes",
        {},
        {
          storage: "sessionStorage",
        },
      );

    // Filter data based on selected filters
    const filteredData = useMemo(() => {
      return filterActivityData(data, filterMode, filters);
    }, [data, filterMode, filters]);

    // Get the buying group selection context
    const { selectedPersonIds, buyingGroupMode } = useBuyingGroupSelection();

    // Use the API hook with proper parameters (or use override for Person Explorer)
    // IMPORTANT: This implements a dual-filtering approach for custom buying groups:
    // 1. We continue using personIds for touchpoint filtering (showing all related touchpoints)
    // 2. We use customGroupOpportunityIds for opportunity display (showing only explicitly selected opportunities)
    //
    // This ensures users see all relevant touchpoint data while having precise control over which
    // opportunity stages are displayed in the timeline visualization.
    const { data: opportunitiesFromApi } = useAccountOpportunities(
      opportunitiesOverride ? undefined : accountId, // Skip fetch if using override
      selectedOpportunityIds,
      selectedPersonIds.length > 0 ? selectedPersonIds : undefined,
      buyingGroupMode, // Pass the buying group mode
      // Pass custom group opportunity IDs for explicit opportunity selection in custom mode
      buyingGroupMode === "custom" ? customGroupOpportunityIds : undefined,
    );

    // Use override if provided, otherwise use API data
    const opportunities = opportunitiesOverride ?? opportunitiesFromApi;

    // Fetch milestone analysis
    const milestoneAnalysis = useMemo(() => {
      // If admin, show all analyses; otherwise show only those with an active version
      return milestoneAnalysisEnabled
        ? milestoneTasks
        : milestoneTasks?.filter((group) =>
            group.analyses.find((a) => a.is_active_version),
          );
    }, [milestoneTasks, milestoneAnalysisEnabled]);

    // Process opportunity transitions
    const allStageTransitions = useMemo(
      () => processOpportunityTransitions(opportunities),
      [opportunities],
    );

    // Associate milestone analysis with transitions using utility function
    const stageTransitionsWithMoments = useMemo(
      () =>
        associateMilestoneAnalysesWithTransitions(
          allStageTransitions,
          milestoneAnalysis,
        ),
      [allStageTransitions, milestoneAnalysis],
    );

    // Find the TaskGroup to display based on the selected ID
    const selectedMilestoneTask = useMemo(() => {
      if (!selectedMilestoneTaskId) return undefined;
      return milestoneTasks?.find(
        (tg) => tg.task_id === selectedMilestoneTaskId,
      );
    }, [milestoneTasks, selectedMilestoneTaskId]);

    // Set initial selected analysis when the task changes
    useEffect(() => {
      if (selectedMilestoneTask) {
        // First look for the active version
        const activeAnalysis = selectedMilestoneTask.analyses.find(
          (a) => a.is_active_version,
        );
        if (activeAnalysis) {
          setSelectedAnalysis(activeAnalysis);
        } else if (selectedMilestoneTask.analyses.length > 0) {
          // Otherwise take the first one
          setSelectedAnalysis(selectedMilestoneTask.analyses[0]);
        }
      }
    }, [selectedMilestoneTask]);

    // Merge transitions with activities
    const mergedData = useMemo(
      () =>
        mergeTransitionsWithActivities(
          filteredData,
          stageTransitionsWithMoments,
        ),
      [filteredData, stageTransitionsWithMoments],
    );

    const { collapsedData, toggleExpand } = useCollapsedData(mergedData);

    // Thread metadata for creating connector lines between activities
    const { getThreadInfo } = useThreadMetadata(
      selectedActivity,
      mergedData,
      selectedAnalysis,
    );

    // For mapping ids to display names
    const {
      getPersonName: getPersonNameFromApi,
      getPersonEmail: getPersonEmailFromApi,
    } = useTouchpointPersons(
      getPersonNameOverride || getPersonEmailOverride ? undefined : accountId,
    );
    const { getTeamDisplayName } = useTeamDisplayNames();

    // Use overrides if provided, otherwise use API functions
    const getPersonName = getPersonNameOverride ?? getPersonNameFromApi;
    const getPersonEmail = getPersonEmailOverride ?? getPersonEmailFromApi;

    // Get filter options
    const filterOptions = useMemo(() => {
      // Create filter options for channels
      const channelSet = new Set<string>();
      const teamSet = new Set<string>();
      const statusSet = new Set<string>();
      const peopleSet = new Set<string>();

      data.forEach((activity) => {
        if (activity.channel) {
          channelSet.add(activity.channel);
        }
        if (activity.involved_team_ids) {
          activity.involved_team_ids.forEach((teamId) => teamSet.add(teamId));
        }
        if (activity.status) {
          statusSet.add(activity.status);
        }
        if (activity.people) {
          activity.people.forEach((person) => peopleSet.add(person.id));
        }
      });

      return {
        channels: Array.from(channelSet).map((channel) => ({
          id: channel,
          label: channel,
        })),
        teams: Array.from(teamSet).map((teamId) => ({
          id: teamId,
          label: getTeamDisplayName(teamId),
        })),
        statuses: Array.from(statusSet).map((status) => ({
          id: status,
          label: status,
        })),
        people: Array.from(peopleSet).map((personId) => {
          const name = getPersonName(personId);
          const email = getPersonEmail(personId);

          // Check if name is already an email address (the fallback case)
          // Only add email in parentheses if it's different from the name
          const isNameEmail = name.includes("@");
          const showEmailSeparately = email && !isNameEmail;

          return {
            id: personId,
            label: name + (showEmailSeparately ? ` (${email})` : ""),
          };
        }),
        direction: [
          { id: "IN", label: "Engagement (incoming touchpoints)" },
          { id: "OUT", label: "Bid (outgoing touchpoints)" },
        ],
      };
    }, [data, getPersonName, getTeamDisplayName, getPersonEmail]);

    // Use getPersonName and getTeamDisplayName to display filter labels for select filters
    const activityColumns = useMemo(
      () => getActivityColumns(filterOptions),
      [filterOptions],
    );

    // Derive display filters using the new utility name
    const displayFilters = useMemo(() => {
      const { citedTouchpointIds: _, ...rest } = filters; // Destructure to get 'rest', explicitly ignore citedTouchpointIds
      return mapDisplayFilters(rest, getPersonName, getTeamDisplayName);
    }, [filters, getTeamDisplayName, getPersonName]);

    // Tanstack table columns
    const columns = useActivityTableColumns(filterOptions);

    // ----- Virtualization & Scroll -----
    // Insert gap rows between activities for virtualization & rendering
    const gappedData = useMemo(
      () => insertGapRows(collapsedData),
      [collapsedData],
    );

    const rowVirtualizer = useVirtualizedRows(
      tableRef as React.RefObject<HTMLElement>,
      gappedData,
    );

    const scrollToDate = useScrollToDate(tableRef, gappedData, rowVirtualizer);

    // Pass callback to update local visible range state
    useVisibleRange(
      tableRef as React.RefObject<HTMLDivElement>,
      collapsedData as CollapsedActivity[],
      (range: { start: string; end: string }) => {
        setVisibleDateRange(range);
        onVisibleRangeChange?.(range);
      },
    );

    // Report filtered activities to parent when they change
    useEffect(() => {
      if (onFilteredActivitiesChange) {
        // Extract only Activity items (not collapsed groups) when reporting to parent
        const filtered = collapsedData
          .filter((item) => !("activities" in item)) // Only include direct activities, not collapsed groups
          .map((item) => item as Activity);

        onFilteredActivitiesChange(filtered);
      }
      // Use the unfiltered list provided by the parent for CTA logic
    }, [collapsedData, onFilteredActivitiesChange]);

    // Use the CTA visibility hook to determine when to show scroll CTAs
    const { showAboveCTA, showBelowCTA, nearestAboveDate, nearestBelowDate } =
      useCTAVisibility(
        filterMode,
        filters,
        filteredActivities,
        collapsedData,
        visibleDateRange,
      );

    // Initialize table with resizing capability
    const table = useReactTable({
      data: collapsedData,
      columns: columns.map((col) => ({
        ...col,
        enableResizing: true, // Allow resizing all columns
      })) as ColumnDef<Activity | CollapsedActivity>[],
      state: { columnSizing },
      onColumnSizingChange: setColumnSizing,
      columnResizeMode: "onChange",
      getCoreRowModel: getCoreRowModel(),
      defaultColumn: {
        minSize: 20,
        size: 150, // Default column size
      },
    });

    // Function to handle filtering by cited touchpoints
    const handleFilterCitedTouchpoints = (
      taskId: string,
      touchpointIds: string[],
    ) => {
      // Clear any existing filters first
      clearAllFilters();
      // Update the filter state with a special key that means "show only these IDs"
      updateFilter("citedTouchpointIds", touchpointIds);
      // Open the milestone analysis side sheet for the given task
      setSelectedActivity(null);
      setFocusedActivityId(undefined);
      setSelectedMilestoneTaskId(taskId);
      setIsDetailsOpen(true);
    };

    // ===== Event Handlers =====
    const handleActivityClick = useCallback(
      (activity: Activity | CollapsedActivity) => {
        // Reset any selected milestone analysis when clicking an activity
        setSelectedMilestoneTaskId(undefined);
        setSelectedAnalysis(undefined);

        const activityId = activity.id;
        const isCurrentlySelected = isGroupedActivity(activity)
          ? selectedActivity?.id === activityId
          : focusedActivityId === activityId;

        // If clicking the currently selected activity, close the sheet and clear selection
        if (isCurrentlySelected) {
          setIsDetailsOpen(false);
          setSelectedActivity(null);
          setFocusedActivityId(undefined);
          return;
        }

        // Update the activity and focused ID first, then ensure the sheet is open
        const updateActivityState = () => {
          // If it's already a grouped activity, show it as is
          if (
            activity &&
            "record_type" in activity &&
            activity.record_type === "web_page_view"
          ) {
            setSelectedActivity(activity);
            setFocusedActivityId(activity.id);
          } else if (isGroupedActivity(activity)) {
            setSelectedActivity(activity);
            setFocusedActivityId(undefined);
          } else {
            // Check if this activity is part of an existing group
            const existingGroup = collapsedData.find(
              (item): item is CollapsedActivity =>
                isGroupedActivity(item) &&
                item.activities.some((a) => a.id === activity.id),
            );

            if (existingGroup) {
              setSelectedActivity(existingGroup);
              setFocusedActivityId(activity.id);
            } else {
              // Find related activities
              const relatedActivities = data.filter((act) => {
                if (activity.thread_id || act.thread_id) {
                  return (
                    Boolean(activity.thread_id) &&
                    Boolean(act.thread_id) &&
                    activity.thread_id === act.thread_id
                  );
                }
                return (
                  (Boolean(activity.campaign_id) &&
                    activity.campaign_id === act.campaign_id) ||
                  activity.activity === act.activity
                );
              });

              if (relatedActivities.length > 1) {
                const virtualCollapsedActivity: CollapsedActivity = {
                  id: `virtual-${activity.id}`,
                  activities: relatedActivities,
                  date: activity.date,
                  endDate: relatedActivities[relatedActivities.length - 1].date,
                  activity: activity.activity,
                  channel: activity.channel,
                  status: activity.status,
                  people: activity.people || [],
                  isExpanded: true,
                  direction: activity.direction,
                  involved_team_ids: activity.involved_team_ids || [],
                  source_record_type: activity.source_record_type,
                  source_record_id: activity.source_record_id,
                };
                setSelectedActivity(virtualCollapsedActivity);
              } else {
                setSelectedActivity(activity);
              }
              setFocusedActivityId(activity.id);
            }
          }
        };

        // Update state and ensure sheet is open
        updateActivityState();
        setIsDetailsOpen(true);
        onActivityClick?.(activity);
      },
      [
        data,
        onActivityClick,
        collapsedData,
        selectedActivity,
        focusedActivityId,
      ],
    );

    // Handler for milestone analysis clicks
    const handleMilestoneAnalysisClick = useCallback(
      (MilestoneAnalysis: TaskGroup) => {
        // Close normal activity details if open
        setSelectedActivity(null);
        setFocusedActivityId(undefined);

        // Set the selected milestone analysis Task ID and open the details sheet
        setSelectedMilestoneTaskId(MilestoneAnalysis.task_id);
        setIsDetailsOpen(true);
      },
      [],
    );

    // CTA Click Handlers
    const handleScrollAboveClick = useCallback(() => {
      if (nearestAboveDate) {
        scrollToDate(nearestAboveDate);
      }
    }, [nearestAboveDate, scrollToDate]);

    const handleScrollBelowClick = useCallback(() => {
      if (nearestBelowDate) {
        scrollToDate(nearestBelowDate);
      }
    }, [nearestBelowDate, scrollToDate]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        scrollToDate,
        openMilestoneAnalysis: (taskId: string) => {
          setSelectedActivity(null);
          setFocusedActivityId(undefined);
          setSelectedMilestoneTaskId(taskId);
          setIsDetailsOpen(true);
        },
      }),
      [scrollToDate],
    );

    // Column sizing persistence is handled by the useBrowserStorage hook

    // Function to handle double-click on column resize handle to auto-size
    const handleDoubleClickResize = useCallback(
      createColumnResizeHandler(setColumnSizing, "touchpoints-table"),
      [],
    );

    // Unified function to reset columns and apply auto-expansion
    const resetAndExpandColumns = useCallback(() => {
      // Reset column sizing

      // Reset to default column sizes from definitions
      const defaultColumnSizing: { [key: string]: number } = {};
      activityColumns.forEach((col) => {
        defaultColumnSizing[col.key] = col.size || 150;
      });

      // Set the default sizes first
      setColumnSizing(defaultColumnSizing);

      // Calculate and apply expansion from fresh base sizes
      if (tableRef.current) {
        const containerWidth = tableRef.current.clientWidth;

        // Calculate total width from base column sizes (not current sizing state)
        const baseTotalWidth = activityColumns.reduce(
          (total, col) => total + (col.size || 150),
          0,
        );

        const availableWidth = containerWidth - 2; // border adjustment
        const excessSpace = availableWidth - baseTotalWidth;

        // Only auto-expand if table is narrower than container (excess space > 20px)
        // Don't shrink if table is already wider than container
        if (excessSpace > 20) {
          const expandedSizing = {
            ...defaultColumnSizing,
            activity: (defaultColumnSizing.activity || 300) + excessSpace - 10, // 10px buffer
          };
          setColumnSizing(expandedSizing);
        }
        // If table is wider than container (excessSpace <= 0), keep default sizes as-is
      }

      // Mark as auto-sized
      hasAutoSized.current = true;
    }, [activityColumns, setColumnSizing]);

    // Function for reset button (uses unified logic)
    const handleResetColumnWidths = useCallback(() => {
      resetAndExpandColumns();
    }, [resetAndExpandColumns]);

    // Enhanced autosize handler with Activity column expansion
    const handleAutosizeColumns = useCallback(
      (columnIds: string[]) => {
        // First, auto-size all columns except Activity using standard utility
        const nonActivityColumns = columnIds.filter((id) => id !== "activity");
        const bulkResizeHandler = createBulkColumnResizeHandler(
          setColumnSizing,
          "touchpoints-table",
        );

        // Auto-size non-Activity columns first
        if (nonActivityColumns.length > 0) {
          bulkResizeHandler(nonActivityColumns);
        }

        // Then calculate and set Activity column to fill remaining space
        setTimeout(() => {
          if (tableRef.current) {
            const containerWidth = tableRef.current.clientWidth;

            // Calculate total width of all other columns
            let otherColumnsWidth = 0;
            activityColumns.forEach((col) => {
              if (col.key !== "activity") {
                // Get the newly auto-sized width or default
                const currentWidth =
                  document
                    .querySelector(`[data-column-id="${col.key}"]`)
                    ?.getBoundingClientRect().width ||
                  col.size ||
                  150;
                otherColumnsWidth += currentWidth;
              }
            });

            // Calculate remaining space for Activity column (match Reset Table calculation)
            const availableWidth = containerWidth - 2; // border adjustment
            const activityDefaultWidth = 300; // Default Activity column width
            const excessSpace =
              availableWidth - (otherColumnsWidth + activityDefaultWidth);

            // Only expand if there's excess space (like Reset Table), use same buffer calculation
            const activityWidth =
              excessSpace > 0
                ? activityDefaultWidth + excessSpace - 10 // Same calculation as Reset Table
                : activityDefaultWidth;

            // Set Activity column width
            if (columnIds.includes("activity")) {
              setColumnSizing((prev) => ({
                ...prev,
                activity: activityWidth,
              }));
            }
          }
        }, 50); // Small delay to let the other columns resize first
      },
      [setColumnSizing, activityColumns],
    );

    // Track if we've already done the initial auto-sizing
    const hasAutoSized = useRef(false);

    // Improved auto-sizing using the new expansion logic
    useEffect(() => {
      // We need to wait for the table to be rendered with data
      if (data.length === 0 || isLoading) return;

      // Skip if we've already auto-sized
      if (hasAutoSized.current) return;

      // Skip if there are persisted column sizes - don't override user preferences
      if (Object.keys(columnSizing).length > 0) {
        hasAutoSized.current = true; // Mark as done to prevent future runs
        return;
      }

      // Set a timeout to let the initial render complete
      const timeoutId = setTimeout(() => {
        // Don't auto-expand if user is actively resizing columns
        if (table.getState().columnSizingInfo.isResizingColumn) {
          // Try again in a moment
          hasAutoSized.current = false;
          return;
        }

        // Use the unified reset and expansion logic for initial sizing
        resetAndExpandColumns();
      }, 500); // Wait half a second for rendering to complete

      return () => clearTimeout(timeoutId);
    }, [data.length, isLoading, resetAndExpandColumns, table, columnSizing]);

    // Force a re-render when column sizing changes to keep table body in sync
    useLayoutEffect(() => {
      // This triggers a re-render of the entire table when column sizing changes
      const forceUpdate = () => {
        if (tableRef.current) {
          // Force DOM update to ensure colgroup is rendered with the latest widths
          // Use requestAnimationFrame for smoother updates without layout shifts
          requestAnimationFrame(() => {
            if (tableRef.current) {
              // Trigger a reflow/repaint without changing visual appearance
              tableRef.current.getBoundingClientRect();

              // Request another frame to apply any necessary updates
              requestAnimationFrame(() => {
                // Optional: can add class toggle or other trigger if needed
              });
            }
          });
        }
      };

      // Debounce the update to avoid excessive re-renders during resize
      const timeoutId = setTimeout(forceUpdate, 10);

      return () => clearTimeout(timeoutId);
    }, []);

    // === Auto-scroll horizontally when the table grows wider ===
    const prevTableWidthRef = useRef<number>(table.getTotalSize());

    useEffect(() => {
      const container = tableRef.current;
      if (!container) return;

      // Don't auto-scroll when user is actively resizing columns
      if (
        table.getState().columnSizingInfo.isResizingColumn ||
        table.getState().columnSizingInfo.deltaOffset !== null
      ) {
        // Still update the reference to prevent jumps when resizing stops
        prevTableWidthRef.current = table.getTotalSize();
        return;
      }

      // Current and previous total table width
      const currentWidth = table.getTotalSize();
      const prevWidth = prevTableWidthRef.current;

      // If width increased and the user was scrolled to (or near) the right edge, keep the new area in view
      if (currentWidth > prevWidth) {
        const rightEdge = container.scrollLeft + container.clientWidth;
        const nearRightEdge = rightEdge >= prevWidth - 8; // 8px tolerance

        if (nearRightEdge) {
          // Scroll by the amount the width grew
          container.scrollLeft += currentWidth - prevWidth;
        }
      }

      // Update reference for next run
      prevTableWidthRef.current = currentWidth;
    }, [table]);

    // ===== Render Helpers =====
    const renderLoadingState = () => (
      <TableRow>
        <TableCell colSpan={9}>
          <div className="ml-[20px] space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        </TableCell>
      </TableRow>
    );

    const renderEmptyState = () => (
      <TableRow>
        <TableCell
          colSpan={columns.length}
          className="h-24 text-center text-muted-foreground"
        >
          No activities found
        </TableCell>
      </TableRow>
    );

    const renderRow = (
      item: Activity | CollapsedActivity | GapRow,
      index: number,
    ) => {
      if (!item) return null;

      // ----- Render Gap Row -----
      if ("kind" in item && item.kind === "gap") {
        return (
          <GapRowComponent
            key={item.id}
            ref={
              rowVirtualizer.measureElement as React.Ref<HTMLTableRowElement>
            }
            item={item}
            index={index}
          />
        );
      }

      // ----- Render Transition Row (unchanged) / Activity Row -----
      const activityItem = item as Activity | CollapsedActivity;
      const isExpanded =
        "activities" in activityItem ? activityItem.isExpanded : false;
      const threadInfo = getThreadInfo(activityItem, isExpanded);

      // Handle opportunity transition rows
      if ("isOpportunityTransition" in item && item.isOpportunityTransition) {
        const opportunityItem = item as unknown as OpportunityTransitionItem;
        const transition = opportunityItem.opportunityTransition;
        return (
          <OpportunityStageTransitionRow
            key={item.id}
            index={index}
            ref={
              rowVirtualizer.measureElement as React.Ref<HTMLTableRowElement>
            }
            transition={transition}
            opportunityName={transition.opportunityName}
            {...threadInfo}
            onMilestoneAnalysisClick={handleMilestoneAnalysisClick}
          />
        );
      }

      return (
        <React.Fragment key={item.id}>
          {/* Main activity row */}
          <ActivityRow
            index={index}
            ref={
              rowVirtualizer.measureElement as React.Ref<HTMLTableRowElement>
            }
            item={activityItem}
            isExpanded={isExpanded}
            onToggleExpand={toggleExpand}
            onActivityClick={handleActivityClick}
            className={getRowClassName(
              activityItem,
              isExpanded,
              filterMode,
              filters,
              selectedActivity,
              focusedActivityId,
            )}
            accountId={accountId}
            {...threadInfo}
            selectedPeople={(filters.people as string[]) || []}
            getPersonNameOverride={getPersonNameOverride}
            getPersonEmailOverride={getPersonEmailOverride}
          />

          {/* Expanded sub-activities */}
          {"activities" in activityItem &&
            activityItem.isExpanded &&
            (activityItem as CollapsedActivity).activities.map(
              (activity: Activity) => {
                const subThreadInfo = getThreadInfo(activity, true);
                return (
                  <ActivityRow
                    index={index}
                    ref={
                      rowVirtualizer.measureElement as React.Ref<HTMLTableRowElement>
                    }
                    key={activity.id}
                    item={activity}
                    isExpanded={true}
                    onActivityClick={handleActivityClick}
                    className={cn(
                      getRowClassName(
                        activity,
                        true,
                        filterMode,
                        filters,
                        selectedActivity,
                        focusedActivityId,
                      ),
                      activity.id !== focusedActivityId
                        ? "border-0 bg-muted border-l-2 border-l-primary"
                        : "",
                    )}
                    accountId={accountId}
                    {...subThreadInfo}
                    selectedPeople={(filters.people as string[]) || []}
                    getPersonNameOverride={getPersonNameOverride}
                    getPersonEmailOverride={getPersonEmailOverride}
                  />
                );
              },
            )}
        </React.Fragment>
      );
    };

    // ===== Render Component =====
    // Determine the correct count for the header based on active filters
    const citedTouchpointIds = filters.citedTouchpointIds as
      | string[]
      | undefined;
    const displayCount = citedTouchpointIds
      ? citedTouchpointIds.length // Use cited IDs length if that filter is active
      : filteredActivities.length; // Otherwise use the length from the prop
    return (
      <>
        <div className="relative min-h-0 flex flex-1 flex-col rounded-b-lg rounded-t-none corner-smooth-lg border border-t-0 bg-card text-card-foreground shadow-sm">
          {/* Touchpoints Header with Active Filters */}
          <div className="flex items-center justify-between sticky top-0 z-20 px-6 py-2 overflow-x-auto bg-card border-b">
            <div className="flex items-center gap-2 shrink-0 h-7">
              <h2 className="text-foreground">Touchpoints</h2>
              <span className="text-xs text-muted-foreground">
                {isLoading
                  ? ""
                  : Object.keys(filters).length === 0 // If no filters are active
                    ? `[${totalTouchpoints || data.length || 0} total]` // Show total count
                    : `${displayCount} match${
                        // Show regular filter count
                        displayCount !== 1 ? "es" : ""
                      } in [${totalTouchpoints || data.length || 0} total]`}
              </span>
              {/* Conditional Badge for Citations - styled like ActiveFiltersDisplay */}
              {citedTouchpointIds && (
                <div className="flex items-center gap-1 bg-amber-100 border text-amber-700 px-2 py-1 rounded-md text-xs">
                  <span>{`Milestone Analysis Citations (${displayCount})`}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 p-0 text-amber-700 hover:bg-amber-100 hover:text-amber-800 ml-1"
                    onClick={() => clearFilter("citedTouchpointIds")}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {/* Active Filters Display inline with the header */}
              <div className="flex items-center gap-2">
                {Object.keys(displayFilters).length > 0 && (
                  <div className="-mb-2 overflow-x-auto">
                    <ActiveFiltersDisplay
                      filters={displayFilters}
                      columns={activityColumns}
                      clearFilter={clearFilter}
                      clearAllFilters={clearAllFilters}
                      clearAllText="Clear all filters"
                      filterType="table"
                    />
                  </div>
                )}
                <FilterModeDropdown
                  filterMode={filterMode}
                  filters={filters}
                  onFilterModeChange={setFilterMode}
                />
              </div>
            </div>

            {/* Right side with autosize and reset buttons */}
            <div className="flex items-center gap-1">
              {/* Autosize All Columns Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      const visibleColumnIds = activityColumns
                        .filter(
                          (col) =>
                            !["gutter", "direction", "expand", "date"].includes(
                              col.key,
                            ),
                        )
                        .map((col) => col.key);
                      handleAutosizeColumns(visibleColumnIds);
                    }}
                  >
                    <UnfoldHorizontal className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Resize all columns to fit their content</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={handleResetColumnWidths}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset column widths</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Main Table Container - with sticky header and scrollable body */}
          <div
            ref={tableRef}
            className={cn(
              "min-h-0 flex flex-1 relative overflow-auto",
              table.getState().columnSizingInfo.isResizingColumn &&
                "table-resizing cursor-col-resize",
            )}
            style={{
              maxWidth: isMobile
                ? "calc(100vw - 2rem)"
                : state === "collapsed"
                  ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                  : "calc(100vw - var(--sidebar-width) - 2rem)",
            }}
          >
            <div style={{ width: table.getTotalSize() }}>
              <Table
                className="w-full table-fixed"
                style={{ width: table.getTotalSize() }}
                data-table-id="touchpoints-table"
                data-table-container
              >
                {/* Colgroup to lock column widths irrespective of virtualization */}
                <colgroup>
                  {activityColumns.map((column) => {
                    // Priority:
                    // 1. User-saved size from localStorage (columnSizing)
                    // 2. Static size from column definition (column.size)
                    // 3. Default size from tanstack table

                    const colWidth = Math.max(
                      columnSizing[column.key] ??
                        column.size ??
                        table.getColumn(column.key)?.getSize() ??
                        150,
                      20,
                    );

                    return (
                      <col
                        key={column.key}
                        style={{
                          width: `${colWidth}px`,
                        }}
                      />
                    );
                  })}
                </colgroup>

                {/* Fixed Header */}
                <ActivityTableHeader
                  table={table}
                  activityColumns={activityColumns}
                  filters={filters as Record<string, FilterValue>}
                  updateFilter={updateFilter}
                  clearFilter={clearFilter}
                  onColumnResizeDoubleClick={handleDoubleClickResize}
                />

                <TableBody className="[&_tr]:border-0">
                  {isLoading || (collapsedData.length === 0 && data.length > 0)
                    ? renderLoadingState()
                    : collapsedData.length
                      ? (() => {
                          const virtualRows = rowVirtualizer.getVirtualItems();
                          if (!virtualRows.length) return null;

                          const paddingTop = virtualRows[0]?.start ?? 0;
                          const paddingBottom =
                            rowVirtualizer.getTotalSize() -
                            (virtualRows[virtualRows.length - 1]?.end ?? 0);

                          return (
                            <>
                              {paddingTop > 0 && (
                                <TableRow style={{ height: paddingTop }}>
                                  <TableCell
                                    colSpan={columns.length}
                                    style={{ height: paddingTop, padding: 0 }}
                                  />
                                </TableRow>
                              )}

                              {virtualRows.map((virtualRow) =>
                                renderRow(
                                  gappedData[virtualRow.index],
                                  virtualRow.index,
                                ),
                              )}

                              {paddingBottom > 0 && (
                                <TableRow style={{ height: paddingBottom }}>
                                  <TableCell
                                    colSpan={columns.length}
                                    style={{
                                      height: paddingBottom,
                                      padding: 0,
                                    }}
                                  />
                                </TableRow>
                              )}
                            </>
                          );
                        })()
                      : renderEmptyState()}
                </TableBody>
              </Table>
            </div>
          </div>
          {/* Activity CTAs */}
          {showAboveCTA && (
            <ScrollCTA
              position="top"
              onClick={handleScrollAboveClick}
              className="top-24" // Further increased offset for headers
            />
          )}
          {showBelowCTA && (
            <ScrollCTA position="bottom" onClick={handleScrollBelowClick} /> // Bottom stays near edge
          )}
        </div>

        <ActivityDetailsSheet
          accountId={
            selectedActivity &&
            "account_id" in selectedActivity &&
            selectedActivity.account_id
              ? selectedActivity.account_id
              : accountId
          }
          getPersonNameOverride={getPersonNameOverride}
          getPersonEmailOverride={getPersonEmailOverride}
          getNameByEmailOverride={getNameByEmailOverride}
          activity={selectedActivity}
          isOpen={isDetailsOpen}
          onOpenChange={(open) => {
            // Only close if we're actually closing, not if we're switching activities
            if (!open && !document.querySelector("[data-row-id]:hover")) {
              setIsDetailsOpen(false);
              setSelectedActivity(null);
              setFocusedActivityId(undefined);
              setSelectedMilestoneTaskId(undefined); // Clear selected task ID on close
              setSelectedAnalysis(undefined);
            }
          }}
          focusedActivityId={focusedActivityId}
          allActivities={data}
          milestoneTask={selectedMilestoneTask} // Pass the derived TaskGroup
          selectedAnalysis={selectedAnalysis}
          setSelectedAnalysis={setSelectedAnalysis}
          onFilterCitedTouchpoints={handleFilterCitedTouchpoints}
          hasCitedTouchpointsFilter={!!citedTouchpointIds}
        />
      </>
    );
  },
);

// Add display name for better debugging
ActivityTable.displayName = "ActivityTable";
